import useMenu from '@hooks/useMenu'
import useModal from '@hooks/useModal'
import { Styles } from '@types'
import { Space } from 'antd'
import dayjs from 'dayjs'
import { BranchDashboardDayEvent, BranchDashboardEventSummary } from 'gadjet-v2-types/dist/type'
import { useEffect, useMemo, useState } from 'react'

import DashboardAPI from '@apis/branch/dashboard'

import DashboardCalendar from '@components/organisms/Branch/Dashboard/DashboardCalendar'
import DashboardMonthPicker from '@components/organisms/Branch/Dashboard/DashboardMonthPicker'
import DashboardSummary from '@components/organisms/Branch/Dashboard/DashboardSummary'
import DashboardDayEventModal from '@components/organisms/Branch/Dashboard/Modals/DashboardDayEventModal'

export default function DashboardPage(): JSX.Element {
  const { hqId, branchId, hqRole } = useMenu()

  const [month, setMonth] = useState(dayjs().format('YYYY-MM'))
  const [eventState, setEventState] = useState<{
    loading: boolean
    events: { date: string; event: BranchDashboardDayEvent }[]
  }>({
    loading: false,
    events: [],
  })
  const [summaryState, setSummaryState] = useState<{ loading: boolean; summary: BranchDashboardEventSummary }>({
    loading: false,
    summary: {
      billingCount: 0,
      contractEndCount: 0,
      contractStartCount: 0,
      expectSalePrice: 0,
      expenditurePrice: 0,
      rentalReservationCount: 0,
      salePrice: 0,
      unpaidPrice: 0,
    },
  })

  const [eventModal, onVisibleEventModal, onCloseEventModal] = useModal({
    visible: false,
    date: dayjs().format('YYYY-MM-DD'),
  })

  const onClickDate = (_date: string) => onVisibleEventModal({ date: _date })

  const dateRange: [string, string] = useMemo(
    () => [
      dayjs(month, 'YYYY-MM').startOf('month').format('YYYY-MM-DD'),
      dayjs(month, 'YYYY-MM').endOf('month').format('YYYY-MM-DD'),
    ],
    [month]
  )

  const getEvents = async () => {
    setEventState({ ...eventState, loading: true })
    const { data } = await DashboardAPI.getDashboardEvents({ hqId, branchId }, { date: dateRange })
    setEventState({ loading: false, events: data })
  }

  const getEventSummary = async () => {
    setSummaryState({ ...summaryState, loading: true })
    const { data } = await DashboardAPI.getDashboardEventSummary({ hqId, branchId }, { date: dateRange })
    setSummaryState({ loading: false, summary: data })
  }

  useEffect(() => {
    getEvents()
    getEventSummary()
  }, [dateRange])

  return (
    <Space size={20} direction="vertical" style={styles.space}>
      <DashboardMonthPicker month={month} onChange={setMonth} />
      <DashboardSummary summary={summaryState.summary} loading={summaryState.loading} />
      <DashboardCalendar
        dateRange={dateRange}
        events={eventState.events}
        loading={eventState.loading}
        onClickDate={onClickDate}
      />

      <DashboardDayEventModal
        hqId={hqId}
        branchId={branchId}
        date={eventModal.date}
        visible={eventModal.visible}
        onClose={onCloseEventModal}
      />
    </Space>
  )
}

const styles: Styles = {
  space: { width: '100%' },
}
